/**
 * StepUpPage - Step-up verification for sensitive actions
 *
 * This page is opened in a popup by third-party apps via the SDK.
 * The user verifies their identity, and the token is returned to the opener.
 */

import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { getApiUrl } from '../api/client'
import { useOrg } from '../hooks/useOrg'
import { AuthLayout } from '../components/layout/AuthLayout'
import { PoweredByFooter } from '../components/OrgBranding'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { AppWindow, Shield, Key, Mail, AlertTriangle, X } from 'lucide-react'
import { PasskeyVerify } from '../components/PasskeyVerify'
import { isPasskeyAvailable } from '../utils/webauthn'

interface ChallengeInfo {
  id: string
  app_id: string
  app_name: string
  app_logo_url: string | null
  action_name: string
  action_description: string
  display_text: string | null
  verification_level: string
  status: string
  expires_at: string
  available_methods: string[]
  redirect_uri?: string // Origin for postMessage
}

interface VerifyResponse {
  success: boolean
  token?: string
  error?: string
}

// API_URL is computed dynamically via getApiUrl() for org-scoped subdomains

export function StepUpPage() {
  const { challengeId } = useParams<{ challengeId: string }>()
  const orgContext = useOrg()
  const [challenge, setChallenge] = useState<ChallengeInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null)
  const [verifying, setVerifying] = useState(false)

  // TOTP state
  const [totpCode, setTotpCode] = useState('')

  // Email OTP state
  const [emailSent, setEmailSent] = useState(false)
  const [emailCode, setEmailCode] = useState('')
  const [sendingEmail, setSendingEmail] = useState(false)

  // Passkey state
  const [passkeyAvailable, setPasskeyAvailable] = useState<boolean | null>(null)

  // Check passkey availability on mount
  useEffect(() => {
    isPasskeyAvailable().then(setPasskeyAvailable)
  }, [])

  useEffect(() => {
    if (challengeId) {
      loadChallenge(challengeId)
    } else {
      setError('No challenge ID provided')
      setLoading(false)
    }
  }, [challengeId])

  async function loadChallenge(id: string) {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(`${getApiUrl()}/api/stepup/${id}`, {
        credentials: 'include',
      })

      if (!response.ok) {
        if (response.status === 404) {
          setError('Challenge not found')
        } else if (response.status === 410) {
          setError('Challenge has expired')
        } else if (response.status === 401) {
          // User not authenticated - redirect to login
          window.location.href = `/login?return_to=${encodeURIComponent(window.location.href)}`
          return
        } else {
          const data = await response.json()
          setError(data.detail || 'Failed to load challenge')
        }
        return
      }

      const data = await response.json()
      setChallenge(data)

      // Auto-select method if only one available
      if (data.available_methods.length === 1) {
        setSelectedMethod(data.available_methods[0])
      }
    } catch (err) {
      setError('Failed to load challenge')
    } finally {
      setLoading(false)
    }
  }

  async function handleConfirm() {
    setVerifying(true)
    try {
      const response = await fetch(`${getApiUrl()}/api/stepup/${challengeId}/confirm`, {
        method: 'POST',
        credentials: 'include',
      })

      const data: VerifyResponse = await response.json()
      handleVerifyResult(data)
    } catch (err) {
      setError('Verification failed')
    } finally {
      setVerifying(false)
    }
  }

  async function handleTotpVerify() {
    if (!totpCode || totpCode.length !== 6) {
      setError('Please enter a 6-digit code')
      return
    }

    setVerifying(true)
    try {
      const response = await fetch(`${getApiUrl()}/api/stepup/${challengeId}/totp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: totpCode }),
      })

      const data: VerifyResponse = await response.json()
      handleVerifyResult(data)
    } catch (err) {
      setError('Verification failed')
    } finally {
      setVerifying(false)
    }
  }

  async function handleSendEmailOtp() {
    setSendingEmail(true)
    try {
      const response = await fetch(`${getApiUrl()}/api/stepup/${challengeId}/email`, {
        method: 'POST',
        credentials: 'include',
      })

      if (response.ok) {
        setEmailSent(true)
        setError(null)
      } else {
        const data = await response.json()
        setError(data.detail || 'Failed to send email')
      }
    } catch (err) {
      setError('Failed to send email')
    } finally {
      setSendingEmail(false)
    }
  }

  async function handleEmailVerify() {
    if (!emailCode || emailCode.length !== 6) {
      setError('Please enter a 6-digit code')
      return
    }

    setVerifying(true)
    try {
      const response = await fetch(`${getApiUrl()}/api/stepup/${challengeId}/email/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: emailCode }),
      })

      const data: VerifyResponse = await response.json()
      handleVerifyResult(data)
    } catch (err) {
      setError('Verification failed')
    } finally {
      setVerifying(false)
    }
  }

  function handlePasskeySuccess(token: string) {
    // Passkey verification returns the token directly
    handleVerifyResult({ success: true, token })
  }

  function handlePasskeyError(errorMessage: string) {
    setError(errorMessage)
  }

  function getTargetOrigin(): string {
    // Get origin from challenge redirect_uri, or fallback to opener's origin
    if (challenge?.redirect_uri) {
      try {
        return new URL(challenge.redirect_uri).origin
      } catch {
        // Invalid URL, fall through to opener check
      }
    }
    // If no redirect_uri, don't send message to prevent security issues
    return ''
  }

  function handleVerifyResult(data: VerifyResponse) {
    if (data.success && data.token) {
      // Send success to opener with validated origin
      if (window.opener) {
        const targetOrigin = getTargetOrigin()
        if (targetOrigin) {
          window.opener.postMessage(
            { type: 'stepup_response', success: true, token: data.token },
            targetOrigin
          )
        }
        window.close()
      }
    } else {
      setError(data.error || 'Verification failed')
    }
  }

  function handleCancel() {
    // Send cancellation to opener with validated origin
    if (window.opener) {
      const targetOrigin = getTargetOrigin()
      if (targetOrigin) {
        window.opener.postMessage(
          { type: 'stepup_response', success: false, error: 'Cancelled by user' },
          targetOrigin
        )
      }
      window.close()
    }
  }

  // Loading state
  if (loading) {
    return (
      <AuthLayout>
        <Card className="p-8">
          <div className="flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
          <p className="mt-4 text-center text-[var(--text-secondary)]">
            Loading verification...
          </p>
        </Card>
      </AuthLayout>
    )
  }

  // Error state (no challenge)
  if (error && !challenge) {
    return (
      <AuthLayout>
        <Card className="p-8">
          <div className="mb-4 flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <h2 className="text-center text-lg font-semibold text-[var(--text-primary)]">
            {error}
          </h2>
          <p className="mt-2 text-center text-sm text-[var(--text-secondary)]">
            This verification request is no longer valid.
          </p>
          <div className="mt-6">
            <Button variant="outline" className="w-full" onClick={handleCancel}>
              Close
            </Button>
          </div>
        </Card>
      </AuthLayout>
    )
  }

  if (!challenge) {
    return null
  }

  // Render method selection or verification form
  return (
    <AuthLayout>
      <Card className="overflow-hidden">
        {/* App Header */}
        <div className="border-b border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-4">
          <div className="flex items-center gap-3">
            {challenge.app_logo_url ? (
              <img
                src={challenge.app_logo_url}
                alt={challenge.app_name}
                className="h-10 w-10 rounded-lg"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
                <AppWindow className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
            )}
            <div>
              <h2 className="font-semibold text-[var(--text-primary)]">
                {challenge.app_name}
              </h2>
              <p className="text-xs text-[var(--text-secondary)]">
                Requesting verification
              </p>
            </div>
          </div>
        </div>

        {/* Action Description */}
        <div className="p-6">
          <div className="mb-4">
            <h3 className="font-medium text-[var(--text-primary)]">
              {challenge.action_description}
            </h3>
            {challenge.display_text && (
              <p className="mt-2 text-sm text-[var(--text-secondary)]">
                {challenge.display_text}
              </p>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 rounded-lg bg-[var(--error-muted)]/30 p-3 text-sm text-[var(--error)]">
              {error}
            </div>
          )}

          {/* Method Selection */}
          {!selectedMethod && challenge.available_methods.length > 1 && (
            <div className="space-y-3">
              <p className="text-sm text-[var(--text-secondary)]">
                Choose a verification method:
              </p>

              {challenge.available_methods.includes('totp') && (
                <button
                  onClick={() => setSelectedMethod('totp')}
                  className="flex w-full items-center gap-3 rounded-lg border border-[var(--border-subtle)] p-4 text-left hover:bg-[var(--bg-elevated)]"
                >
                  <Shield className="h-5 w-5 text-primary-600" />
                  <div>
                    <p className="font-medium text-[var(--text-primary)]">
                      Authenticator App
                    </p>
                    <p className="text-sm text-[var(--text-secondary)]">
                      Use your authenticator app
                    </p>
                  </div>
                </button>
              )}

              {challenge.available_methods.includes('passkey') && passkeyAvailable !== false && (
                <button
                  onClick={() => setSelectedMethod('passkey')}
                  disabled={passkeyAvailable === null}
                  className="flex w-full items-center gap-3 rounded-lg border border-[var(--border-subtle)] p-4 text-left hover:bg-[var(--bg-elevated)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Key className="h-5 w-5 text-primary-600" />
                  <div>
                    <p className="font-medium text-[var(--text-primary)]">Passkey</p>
                    <p className="text-sm text-[var(--text-secondary)]">
                      {passkeyAvailable === null
                        ? 'Checking availability...'
                        : 'Use your security key or biometrics'}
                    </p>
                  </div>
                </button>
              )}

              {challenge.available_methods.includes('email') && (
                <button
                  onClick={() => setSelectedMethod('email')}
                  className="flex w-full items-center gap-3 rounded-lg border border-[var(--border-subtle)] p-4 text-left hover:bg-[var(--bg-elevated)]"
                >
                  <Mail className="h-5 w-5 text-primary-600" />
                  <div>
                    <p className="font-medium text-[var(--text-primary)]">Email Code</p>
                    <p className="text-sm text-[var(--text-secondary)]">
                      Send a verification code to your email
                    </p>
                  </div>
                </button>
              )}
            </div>
          )}

          {/* Confirm Level - Just an Approve button */}
          {challenge.verification_level === 'confirm' && (
            <div className="space-y-4">
              <Button
                className="w-full"
                onClick={handleConfirm}
                disabled={verifying}
              >
                {verifying ? 'Verifying...' : 'Approve'}
              </Button>
            </div>
          )}

          {/* TOTP Verification */}
          {selectedMethod === 'totp' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary-600" />
                <span className="font-medium text-[var(--text-primary)]">
                  Authenticator App
                </span>
              </div>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="Enter 6-digit code"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-lg tracking-widest"
              />
              <Button
                className="w-full"
                onClick={handleTotpVerify}
                disabled={verifying || totpCode.length !== 6}
              >
                {verifying ? 'Verifying...' : 'Verify'}
              </Button>
              {challenge.available_methods.length > 1 && (
                <button
                  onClick={() => {
                    setSelectedMethod(null)
                    setTotpCode('')
                    setError(null)
                  }}
                  className="w-full text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                >
                  Try a different method
                </button>
              )}
            </div>
          )}

          {/* Email OTP Verification */}
          {selectedMethod === 'email' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary-600" />
                <span className="font-medium text-[var(--text-primary)]">
                  Email Verification
                </span>
              </div>

              {!emailSent ? (
                <Button
                  className="w-full"
                  onClick={handleSendEmailOtp}
                  disabled={sendingEmail}
                >
                  {sendingEmail ? 'Sending...' : 'Send Code to Email'}
                </Button>
              ) : (
                <>
                  <p className="text-sm text-[var(--text-secondary)]">
                    We sent a code to your email. Enter it below:
                  </p>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="Enter 6-digit code"
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-lg tracking-widest"
                  />
                  <Button
                    className="w-full"
                    onClick={handleEmailVerify}
                    disabled={verifying || emailCode.length !== 6}
                  >
                    {verifying ? 'Verifying...' : 'Verify'}
                  </Button>
                  <button
                    onClick={handleSendEmailOtp}
                    disabled={sendingEmail}
                    className="w-full text-sm text-[var(--text-primary)] hover:opacity-80 transition-opacity"
                  >
                    {sendingEmail ? 'Sending...' : 'Resend code'}
                  </button>
                </>
              )}

              {challenge.available_methods.length > 1 && (
                <button
                  onClick={() => {
                    setSelectedMethod(null)
                    setEmailSent(false)
                    setEmailCode('')
                    setError(null)
                  }}
                  className="w-full text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                >
                  Try a different method
                </button>
              )}
            </div>
          )}

          {/* Passkey Verification */}
          {selectedMethod === 'passkey' && challengeId && (
            <PasskeyVerify
              challengeId={challengeId}
              onSuccess={handlePasskeySuccess}
              onError={handlePasskeyError}
              onBack={() => {
                setSelectedMethod(null)
                setError(null)
              }}
              hasOtherMethods={challenge.available_methods.length > 1}
              autoTrigger={passkeyAvailable === true}
            />
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[var(--border-subtle)] p-4">
          <button
            onClick={handleCancel}
            className="flex w-full items-center justify-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            <X className="h-4 w-4" />
            Cancel
          </button>
        </div>

        {/* Powered by footer for org-branded step-up */}
        {orgContext.slug && <PoweredByFooter />}
      </Card>
    </AuthLayout>
  )
}
